import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Dialog,
  Grid,
  Icon,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Select,
  Snackbar,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import WhatsAppMessageComposer from "app/wacc/components/message_composer";
import WhatsAppTemplateComposer from "app/wacc/components/template_message_composer";
import { useAwaitDialog } from "app/components/status_dialog";
import countryCodes from "app/assets/country_codes";
import MDInput from "components/MDInput";

function WhatsAppNumberConversationsPage() {
  const pageTitle = "WhatsApp - Conversations";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [showMessagesLoading, setShowMessagesLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [conversationSearch, setConversationSearch] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyTo, setReplyTo] = useState(null);

  const [emojiCategories, setEmojiCategories] = useState([]);
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [reactionMessage, setReactionMessage] = useState(null);

  const messagesEndRef = useRef(null);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const [newConversationDialogOpen, setNewConversationDialogOpen] =
    useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    country_code: "234",
    phone_number: "",
  });
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  useEffect(() => {
    fetch("/assets/emojis.json")
      .then((response) => response.json())
      .then((data) => {
        setEmojiCategories(data || []);
      })
      .catch(() => {
        setEmojiCategories([]);
      });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/agents/wacc/numbers/${ref}/conversations.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setConversations(result.data.data.conversations || []);
            setTemplates(result.data.data.templates || []);
            setPageLoaded(true);
          } else {
            setErrorMsg(result.data.data.message);
            setSeverity("error");
            setOpen(true);
          }
        })
        .catch(async () => {
          setShowLoading(false);

          setErrorMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setSeverity("error");
          setOpen(true);
        });
    };

    fetchData();
  }, [ref]);

  const getMessages = async (conversation) => {
    setShowMessagesLoading(true);

    await axios
      .get(
        `/agents/wacc/numbers/conversations/${conversation.reference}/messages.json`,
      )
      .then((result) => {
        setShowMessagesLoading(false);

        if (result.data.status === "success") {
          setMessages(result.data.data.messages || []);
          setConversationDetails(result.data.data.conversation || null);
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(async () => {
        setShowMessagesLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setSeverity("error");
        setOpen(true);
      });
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setConversationDetails(null);
    setMessages([]);
    setReplyTo(null);
    setReactionAnchor(null);
    setReactionMessage(null);
    setTemplateDialogOpen(false);

    await getMessages(conversation);
  };

  useEffect(() => {
    if (selectedConversation && !showMessagesLoading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "auto",
      });
    }
  }, [selectedConversation, showMessagesLoading, messages]);

  const handleBackToConversations = () => {
    setSelectedConversation(null);
    setConversationDetails(null);
    setMessages([]);
    setReplyTo(null);
    setReactionAnchor(null);
    setReactionMessage(null);
    setTemplateDialogOpen(false);
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  const openReactionPicker = (event, message) => {
    setReactionAnchor(event.currentTarget);
    setReactionMessage(message);
  };

  const closeReactionPicker = () => {
    setReactionAnchor(null);
    setReactionMessage(null);
  };

  const handleMessageReaction = async (emoji) => {
    if (!reactionMessage) {
      return;
    }

    const message = reactionMessage;
    const reactionEmoji = message.reaction?.emoji === emoji ? "" : emoji;

    closeReactionPicker();
    setShowLoading(true);

    await axios
      .post("/agents/wacc/numbers/conversations/messages/reaction.json", {
        message: message.reference,
        emoji: reactionEmoji,
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setMessages((current) =>
            current.map((item) => {
              if (item.reference !== message.reference) {
                return item;
              }

              return {
                ...item,
                reaction: reactionEmoji
                  ? {
                      emoji: reactionEmoji,
                    }
                  : null,
              };
            }),
          );
        } else {
          await statusDialog.show(
            result.data.data.message,
            "error",
            "Reaction Failed",
          );
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Request Failed",
        );
      });
  };

  const formatTime = (value) => {
    if (!value) {
      return "";
    }

    if (value.includes(" ")) {
      return value.split(" ")[0] + " " + value.split(" ")[1].substring(0, 5);
    }

    if (value.includes("T")) {
      return value.split("T")[0] + " " + value.split("T")[1].substring(0, 5);
    }

    return value;
  };

  const getConversationContactName = (conversation) => {
    if (conversation.contact?.name) {
      return conversation.contact.name;
    }

    if (conversation.contact_name) {
      return conversation.contact_name;
    }

    if (conversation.contact?.wa_display_name) {
      return conversation.contact.wa_display_name;
    }

    if (conversation.wa_display_name) {
      return conversation.wa_display_name;
    }

    if (conversation.contact?.phone_number) {
      return conversation.contact.phone_number;
    }

    if (conversation.phone_number) {
      return conversation.phone_number;
    }

    return "Unknown Contact";
  };

  const getConversationPhone = (conversation) => {
    if (conversation.contact?.phone_number) {
      return conversation.contact.phone_number;
    }

    if (conversation.phone_number) {
      return conversation.phone_number;
    }

    return "";
  };

  const getAgentName = (conversation) => {
    if (conversation.agent?.name) {
      return conversation.agent.name;
    }

    if (conversation.agent_name) {
      return conversation.agent_name;
    }

    return "";
  };

  const getLastMessage = (conversation) => {
    if (conversation.last_message) {
      return conversation.last_message;
    }

    if (conversation.message) {
      return conversation.message;
    }

    return "";
  };

  const getInitials = (name) => {
    if (!name) {
      return "?";
    }

    const parts = name.trim().split(" ");

    if (parts.length === 1) {
      return parts[0].substring(0, 1).toUpperCase();
    }

    return (
      parts[0].substring(0, 1) + parts[parts.length - 1].substring(0, 1)
    ).toUpperCase();
  };

  const isCustomerMessage = (message) => {
    return message.author_type === "CUSTOMER";
  };

  const filteredConversations = conversations.filter((conversation) => {
    const search = conversationSearch.trim().toLowerCase();

    if (!search) {
      return true;
    }

    const contactName = getConversationContactName(conversation).toLowerCase();
    const phoneNumber = getConversationPhone(conversation).toLowerCase();
    const agentName = getAgentName(conversation).toLowerCase();
    const lastMessage = getLastMessage(conversation).toLowerCase();

    return (
      contactName.includes(search) ||
      phoneNumber.includes(search) ||
      agentName.includes(search) ||
      lastMessage.includes(search)
    );
  });

  const getMessageText = (message) => {
    if (!message.content) {
      return "";
    }

    if (typeof message.content === "string") {
      return message.content;
    }

    if (message.content.text) {
      return message.content.text;
    }

    if (message.content.body) {
      return message.content.body;
    }

    return "";
  };

  const getMediaUrl = (message) => {
    if (!message.content || typeof message.content !== "object") {
      return "";
    }

    return (
      message.content.url ||
      message.content.link ||
      message.content.media_url ||
      ""
    );
  };

  const downloadMedia = async (message) => {
    try {
      const response = await axios.get(
        `/agents/wacc/numbers/conversations/messages/${message.reference}/media.json`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download =
        message.content?.filename ||
        `${message.content_type}-${message.reference}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      await statusDialog.show(
        "Unable to download media.",
        "error",
        "Download Failed",
      );
    }
  };

  const getMediaDownloadButton = (message) => {
    if (!message.content?.media_id) {
      return null;
    }

    const mediaName =
      message.content?.filename ||
      `${message.content_type}-${message.reference}`;

    return (
      <MDBox
        onClick={() => downloadMedia(message)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          px: 1,
          py: 0.5,
          borderRadius: "6px",
          backgroundColor: "#f0f2f5",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#e4e6e8",
          },
        }}
      >
        <Icon sx={{ fontSize: "16px !important", mr: 0.5 }}>download</Icon>

        <MDTypography
          variant="caption"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {mediaName}
        </MDTypography>
      </MDBox>
    );
  };

  const getReplyPreview = (message) => {
    if (!message.reply_to) {
      return null;
    }

    const reply = message.reply_to;

    return (
      <MDBox
        sx={{
          borderLeft: "3px solid rgba(0, 0, 0, 0.25)",
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: "4px",
          px: 1,
          py: 0.75,
          mb: 1,
        }}
      >
        <MDTypography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {reply.author}
        </MDTypography>

        <MDTypography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            lineHeight: 1.3,
            mt: 0.25,
          }}
        >
          {getMessageText(reply)}
        </MDTypography>
      </MDBox>
    );
  };

  const renderMessageContent = (message) => {
    const contentType = message.content_type;
    const mediaUrl = getMediaUrl(message);

    if (contentType === "template") {
      const template = templates.find(
        (item) => item.reference === message.content?.template_reference,
      );

      if (!template) {
        return (
          <MDTypography sx={{ fontSize: "15px" }}>
            {message.content?.template_code || "Template message"}
          </MDTypography>
        );
      }

      const messageComponents = message.content?.components || [];

      const getComponentParameters = (type) => {
        const component = messageComponents.find(
          (item) => item.type?.toLowerCase() === type.toLowerCase(),
        );

        return component?.parameters || [];
      };

      const replaceComponentVariables = (text, type) => {
        if (!text) {
          return "";
        }

        const parameters = getComponentParameters(type);
        let parameterIndex = 0;

        return text.replace(/{{([^{}]+)}}/g, (match) => {
          const parameter = parameters[parameterIndex];
          parameterIndex += 1;

          return parameter?.text || match;
        });
      };

      return (
        <MDBox>
          {template.components?.map((component, index) => {
            const type = component.type?.toUpperCase();

            if (type === "HEADER") {
              if (component.format === "TEXT") {
                return (
                  <MDTypography
                    key={index}
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {replaceComponentVariables(component.text, "header")}
                  </MDTypography>
                );
              }

              return (
                <MDBox
                  key={index}
                  sx={{
                    mb: 1,
                    p: 1,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                  }}
                >
                  <MDTypography
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                    }}
                  >
                    {component.format || "HEADER"}
                  </MDTypography>
                </MDBox>
              );
            }

            if (type === "BODY") {
              return (
                <MDTypography
                  key={index}
                  sx={{
                    fontSize: "15px",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {replaceComponentVariables(component.text, "body")}
                </MDTypography>
              );
            }

            if (type === "FOOTER") {
              return (
                <MDTypography
                  key={index}
                  sx={{
                    display: "block",
                    fontSize: "12px",
                    color: "text.secondary",
                    mt: 1,
                  }}
                >
                  {replaceComponentVariables(component.text, "footer")}
                </MDTypography>
              );
            }

            if (type === "BUTTONS") {
              return (
                <MDBox
                  key={index}
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    mt: 1.5,
                  }}
                >
                  {component.buttons?.map((button, buttonIndex) => (
                    <MDBox
                      key={buttonIndex}
                      sx={{
                        py: 0.8,
                        textAlign: "center",
                        borderBottom:
                          buttonIndex < component.buttons.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                      }}
                    >
                      <MDTypography
                        sx={{
                          fontSize: "14px",
                          color: "#168c7b",
                          fontWeight: 500,
                        }}
                      >
                        {button.text}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              );
            }

            return null;
          })}
        </MDBox>
      );
    }

    if (contentType === "text") {
      return getMessageText(message);
    }

    if (contentType === "image") {
      return (
        <MDBox>
          {mediaUrl && (
            <img
              src={mediaUrl}
              alt=""
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "300px",
                borderRadius: "6px",
              }}
            />
          )}

          {getMessageText(message) && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {getMessageText(message)}
            </MDTypography>
          )}

          {getMediaDownloadButton(message)}

          {message.content?.caption && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {message.content.caption}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "video") {
      return (
        <MDBox>
          {mediaUrl && (
            <video
              src={mediaUrl}
              controls
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "300px",
                borderRadius: "6px",
              }}
            />
          )}

          {getMessageText(message) && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {getMessageText(message)}
            </MDTypography>
          )}

          {getMediaDownloadButton(message)}

          {message.content?.caption && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {message.content.caption}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "audio") {
      return (
        <MDBox>
          {mediaUrl && (
            <audio
              src={mediaUrl}
              controls
              style={{
                maxWidth: "100%",
              }}
            />
          )}

          {getMediaDownloadButton(message)}
        </MDBox>
      );
    }

    if (contentType === "document") {
      return (
        <MDBox>
          {mediaUrl ? (
            <a
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: "none",
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "15px",
                  fontWeight: 500,
                }}
              >
                {message.content?.filename || "Document"}
              </MDTypography>
            </a>
          ) : (
            <MDTypography sx={{ fontSize: "15px" }}>
              {message.content?.filename || "Document"}
            </MDTypography>
          )}

          {getMessageText(message) && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {getMessageText(message)}
            </MDTypography>
          )}

          {getMediaDownloadButton(message)}

          {message.content?.caption && (
            <MDTypography
              sx={{
                fontSize: "16px",
                mt: 1,
              }}
            >
              {message.content.caption}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "sticker") {
      return (
        <MDBox>
          {mediaUrl && (
            <img
              src={mediaUrl}
              alt=""
              style={{
                display: "block",
                maxWidth: "180px",
                maxHeight: "180px",
              }}
            />
          )}

          {getMediaDownloadButton(message)}
        </MDBox>
      );
    }

    if (contentType === "location") {
      return (
        <MDBox>
          <MDTypography sx={{ fontSize: "15px" }}>Location</MDTypography>

          {message.content?.latitude && message.content?.longitude && (
            <MDTypography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                mt: 0.5,
              }}
            >
              {message.content.latitude}, {message.content.longitude}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "contact") {
      return <MDTypography sx={{ fontSize: "15px" }}>Contact</MDTypography>;
    }

    return getMessageText(message);
  };

  const getMessageStatus = (message) => {
    if (message.read) {
      return "✓✓";
    }

    if (message.delivered) {
      return "✓✓";
    }

    if (message.sent) {
      return "✓";
    }

    return "";
  };

  const isConversationActive = () => {
    if (!conversationDetails?.expires_on) {
      return false;
    }

    return (
      Number(conversationDetails.expires_on) > Math.floor(Date.now() / 1000)
    );
  };

  const handleSendMessage = async (message) => {
    const formData = new FormData();

    formData.append("conversation", selectedConversation.reference);
    formData.append("content_type", message.content_type);
    formData.append("content", message.content || "");
    formData.append("reply_to", replyTo ? replyTo.reference : "");

    if (message.media && message.media.length > 0) {
      message.media.forEach((file) => {
        formData.append("media", file);
      });
    }

    setShowLoading(true);

    await axios
      .post("/agents/wacc/numbers/conversations/send/message.json", formData)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setMessages((current) => [...current, result.data.data.message]);
          setReplyTo(null);
        } else {
          await statusDialog.show(
            result.data.data.message,
            "error",
            "Message Failed",
          );
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Request Failed",
        );
      });
  };

  const handleSendTemplate = async (templateMessage) => {
    const payload = {
      conversation: selectedConversation.reference,
      template: templateMessage.template,
      parameters: templateMessage.components || [],
    };

    setTemplateDialogOpen(false);
    setShowLoading(true);

    await axios
      .post("/agents/wacc/numbers/conversations/send/template.json", payload)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setMessages((current) => [...current, result.data.data.message]);
        } else {
          await statusDialog.show(
            result.data.data.message,
            "error",
            "Template Failed",
          );
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Request Failed",
        );
      });
  };

  const handleNewConversation = () => {
    setNewContact({
      name: "",
      country_code: "234",
      phone_number: "",
    });

    setNewConversationDialogOpen(true);
  };

  const handleNewConversationTemplate = async (templateMessage) => {
    const payload = {
      number: ref,
      name: newContact.name,
      country_code: newContact.country_code,
      phone_number: newContact.phone_number,
      template: templateMessage.template,
      parameters: templateMessage.components || [],
    };

    setNewConversationDialogOpen(false);
    setNewTemplateDialogOpen(false);
    setShowLoading(true);

    await axios
      .post("/agents/wacc/numbers/conversations/create.json", payload)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          const conversation = result.data.data.conversation;
          const message = result.data.data.message;

          setConversations((current) => [
            {
              ...conversation,
              last_message: "",
            },
            ...current,
          ]);

          setNewContact({
            name: "",
            phone_number: "",
          });

          setSelectedConversation(conversation);
          setConversationDetails(conversation);
          setMessages([message]);
          setReplyTo(null);
          setReactionAnchor(null);
          setReactionMessage(null);
        } else {
          await statusDialog.show(
            result.data.data.message,
            "error",
            "Conversation Failed",
          );
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Request Failed",
        );
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={newConversationDialogOpen}
        onClose={() => setNewConversationDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <MDBox px={3} py={2}>
          <MDTypography variant="h6">New Conversation</MDTypography>
        </MDBox>

        <MDBox px={3} pb={2}>
          <MDBox mb={2}>
            <MDTypography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              Contact Name
            </MDTypography>

            <InputBase
              value={newContact.name}
              onChange={(event) =>
                setNewContact((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="Enter contact name"
              fullWidth
              sx={{
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                px: 1.5,
                py: 0.75,
                fontSize: "14px",
              }}
            />
          </MDBox>

          <MDBox>
            <MDTypography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              Phone Number
            </MDTypography>

            <MDBox>
              <Grid container spacing={1}>
                <Grid item xs={3} md={3} lg={3}>
                  <Select
                    fullWidth
                    value={newContact.country_code}
                    onChange={(event) => {
                      setNewContact((current) => ({
                        ...current,
                        country_code: event.target.value,
                      }));
                    }}
                    size="small"
                    sx={{
                      padding: "12px",
                      paddingLeft: "3px",
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem
                        key={`${country.iso}-${country.code}`}
                        value={country.code}
                      >
                        {`${country.iso}: +${country.code}`}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={9} md={9} lg={9}>
                  <MDInput
                    value={newContact.phone_number}
                    onChange={(event) =>
                      setNewContact((current) => ({
                        ...current,
                        phone_number: event.target.value,
                      }))
                    }
                    placeholder="Enter phone number"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox
          px={3}
          py={2}
          sx={{
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <MDButton
            variant="text"
            color="dark"
            onClick={() => setNewConversationDialogOpen(false)}
          >
            Cancel
          </MDButton>

          <MDButton
            variant="contained"
            color="dark"
            disabled={
              !newContact.name.trim() || !newContact.phone_number.trim()
            }
            onClick={() => {
              setNewConversationDialogOpen(false);
              setNewTemplateDialogOpen(true);
            }}
          >
            Continue
          </MDButton>
        </MDBox>
      </Dialog>

      <WhatsAppTemplateComposer
        open={newTemplateDialogOpen}
        onCancel={() => setNewTemplateDialogOpen(false)}
        contact={newContact}
        templates={templates}
        sendTemplate={handleNewConversationTemplate}
      />

      <MDBox mt={0.8} pb={3}>
        <MDBox mb={2} display="flex" justifyContent="space-between">
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/wacc/numbers/${ref}`);
            }}
          >
            Back
          </Button>

          <MDBox display="flex" gap={1}>
            <MDButton
              variant="contained"
              color="dark"
              onClick={handleNewConversation}
              startIcon={<Icon>add</Icon>}
              size="small"
            >
              new
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              sx={{
                height: {
                  xs: "calc(100vh - 120px)",
                  md: "calc(100vh - 180px)",
                },
                overflow: "hidden",
              }}
            >
              <Grid
                container
                sx={{
                  height: "100%",
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={4}
                  lg={4}
                  sx={{
                    height: "100%",
                    borderRight: {
                      xs: "none",
                      md: "1px solid #e0e0e0",
                    },
                    display: {
                      xs: selectedConversation ? "none" : "block",
                      md: "block",
                    },
                  }}
                >
                  <MDBox
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <MDBox
                      px={2}
                      py={2}
                      sx={{
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <MDTypography variant="h6">
                        Conversations ({conversations.length})
                      </MDTypography>
                    </MDBox>

                    <MDBox
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#ffffff",
                          border: "1px solid #e0e0e0",
                          borderRadius: "6px",
                          px: 1,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: "20px !important",
                            color: "text.secondary",
                            mr: 0.75,
                          }}
                        >
                          search
                        </Icon>

                        <InputBase
                          value={conversationSearch}
                          onChange={(event) =>
                            setConversationSearch(event.target.value)
                          }
                          placeholder="Search conversations"
                          fullWidth
                          sx={{
                            fontSize: "13px",
                            py: 0.5,
                          }}
                        />
                      </MDBox>
                    </MDBox>

                    <MDBox
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                      }}
                    >
                      {!pageLoaded && (
                        <MDBox
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <CircularProgress />
                        </MDBox>
                      )}

                      {pageLoaded && conversations.length === 0 && (
                        <MDBox
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 3,
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                          >
                            No conversations found.
                          </MDTypography>
                        </MDBox>
                      )}

                      {pageLoaded &&
                        conversations.length > 0 &&
                        filteredConversations.length === 0 && (
                          <MDBox
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 3,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                              textAlign="center"
                            >
                              No conversations match your search.
                            </MDTypography>
                          </MDBox>
                        )}

                      {pageLoaded &&
                        filteredConversations.map((item) => {
                          const contactName = getConversationContactName(item);
                          const phoneNumber = getConversationPhone(item);
                          const agentName = getAgentName(item);
                          const lastMessage = getLastMessage(item);

                          const selected =
                            selectedConversation?.reference === item.reference;

                          return (
                            <MDBox
                              key={item.reference}
                              onClick={() => selectConversation(item)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 1,
                                py: 1.5,
                                cursor: "pointer",
                                backgroundColor: selected
                                  ? "#f0f2f5"
                                  : "transparent",
                                borderBottom: "1px solid #f0f0f0",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            >
                              <MDBox
                                sx={{
                                  width: 46,
                                  height: 46,
                                  minWidth: 46,
                                  borderRadius: "50%",
                                  backgroundColor: "#e0e0e0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 1.5,
                                }}
                              >
                                <MDTypography
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {getInitials(contactName)}
                                </MDTypography>
                              </MDBox>

                              <MDBox
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <MDBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <MDTypography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      mr: 1,
                                    }}
                                  >
                                    {contactName}
                                  </MDTypography>

                                  <MDTypography
                                    sx={{
                                      fontSize: "10px",
                                      color: "text.secondary",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {formatTime(item.created)}
                                  </MDTypography>
                                </MDBox>

                                <MDTypography
                                  sx={{
                                    display: "block",
                                    color: "text.secondary",
                                    fontSize: "11px",
                                    mt: -0.25,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {lastMessage || phoneNumber || ""}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          );
                        })}
                    </MDBox>
                  </MDBox>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={8}
                  lg={8}
                  sx={{
                    height: "100%",
                    display: {
                      xs: selectedConversation ? "block" : "none",
                      md: "block",
                    },
                  }}
                >
                  <MDBox
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {!selectedConversation && (
                      <MDBox
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f8f9fa",
                          px: 4,
                        }}
                      >
                        <MDBox
                          sx={{
                            textAlign: "center",
                            maxWidth: 400,
                          }}
                        >
                          <MDBox
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 2,
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: "70px !important",
                              }}
                            >
                              forum
                            </Icon>
                          </MDBox>

                          <MDTypography
                            variant="h5"
                            sx={{
                              mb: 1,
                            }}
                          >
                            WhatsApp Conversations
                          </MDTypography>

                          <MDTypography variant="body2" color="text.secondary">
                            Select a conversation to view the messages.
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    )}

                    {selectedConversation && (
                      <>
                        <MDBox
                          sx={{
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <MDBox
                            sx={{
                              display: {
                                xs: "flex",
                                md: "none",
                              },
                              px: 2,
                              py: 1,
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            <Button
                              color="dark"
                              size="small"
                              startIcon={<Icon>west</Icon>}
                              onClick={handleBackToConversations}
                              sx={{
                                minWidth: "auto",
                              }}
                            >
                              Back
                            </Button>
                          </MDBox>

                          <MDBox
                            px={2}
                            py={1.5}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <MDBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <MDBox
                                sx={{
                                  width: 42,
                                  height: 42,
                                  minWidth: 42,
                                  borderRadius: "50%",
                                  backgroundColor: "#e0e0e0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 1.5,
                                }}
                              >
                                <MDTypography
                                  sx={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {getInitials(
                                    getConversationContactName(
                                      selectedConversation,
                                    ),
                                  )}
                                </MDTypography>
                              </MDBox>

                              <MDBox>
                                <MDTypography
                                  sx={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {getConversationContactName(
                                    selectedConversation,
                                  )}
                                </MDTypography>

                                <MDTypography
                                  sx={{
                                    fontSize: "12px",
                                    color: "text.secondary",
                                  }}
                                >
                                  {getConversationPhone(selectedConversation)}
                                </MDTypography>
                              </MDBox>
                            </MDBox>

                            <MDButton
                              variant="outlined"
                              color="dark"
                              onClick={() => getMessages(selectedConversation)}
                              disabled={showMessagesLoading}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                              }}
                              iconOnly
                              size="small"
                            >
                              <Icon>refresh</Icon>
                            </MDButton>
                          </MDBox>
                        </MDBox>

                        <Popover
                          open={Boolean(reactionAnchor)}
                          anchorEl={reactionAnchor}
                          onClose={closeReactionPicker}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          slotProps={{
                            paper: {
                              sx: {
                                width: {
                                  xs: "320px",
                                  sm: "440px",
                                },
                                maxHeight: "420px",
                                overflow: "hidden",
                                backgroundColor: "#ffffff",
                                opacity: 1,
                                borderRadius: "10px",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.20)",
                              },
                            },
                          }}
                        >
                          <MDBox
                            sx={{
                              width: "100%",
                              maxHeight: "420px",
                              overflowY: "auto",
                              overflowX: "hidden",
                              backgroundColor: "#ffffff",
                              py: 1,
                              "&::-webkit-scrollbar": {
                                width: "6px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#c1c1c1",
                                borderRadius: "6px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            {emojiCategories.map((category, categoryIndex) => (
                              <MDBox
                                key={
                                  category.category ||
                                  category.title ||
                                  categoryIndex
                                }
                                sx={{
                                  px: 1.5,
                                  pt: categoryIndex === 0 ? 0.5 : 1,
                                  pb: 0.5,
                                }}
                              >
                                <MDTypography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    mb: 0.75,
                                  }}
                                >
                                  {category.category || category.title}
                                </MDTypography>

                                <MDBox
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "repeat(8, minmax(0, 1fr))",
                                    gap: 0.25,
                                  }}
                                >
                                  {(category.emojis || []).map(
                                    (item, emojiIndex) => (
                                      <button
                                        key={`${categoryIndex}-${emojiIndex}-${item.emoji}`}
                                        onClick={() =>
                                          handleMessageReaction(item.emoji)
                                        }
                                        title={item.title || ""}
                                        style={{
                                          width: "44px",
                                          height: "44px",
                                          padding: 0,
                                          margin: 0,
                                          border: "none",
                                          borderRadius: "4px",
                                          background: "transparent",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          opacity: 1,
                                          filter: "none",
                                          fontSize: "24px",
                                          lineHeight: 1,
                                          fontFamily:
                                            '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
                                        }}
                                      >
                                        {item.emoji}
                                      </button>
                                    ),
                                  )}
                                </MDBox>
                              </MDBox>
                            ))}
                          </MDBox>
                        </Popover>

                        <MDBox
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#efeae2",
                            px: {
                              xs: 1,
                              md: 3,
                            },
                            py: 2,
                          }}
                        >
                          {showMessagesLoading && (
                            <MDBox
                              sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CircularProgress />
                            </MDBox>
                          )}

                          {!showMessagesLoading && messages.length === 0 && (
                            <MDBox
                              sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MDTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                No messages found.
                              </MDTypography>
                            </MDBox>
                          )}

                          {!showMessagesLoading && messages.length > 0 && (
                            <MDBox
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {messages.map((message) => {
                                const customerMessage =
                                  isCustomerMessage(message);
                                const selectedReply =
                                  replyTo?.reference === message.reference;

                                return (
                                  <MDBox
                                    key={message.reference}
                                    sx={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: customerMessage
                                        ? "flex-start"
                                        : "flex-end",
                                    }}
                                  >
                                    <MDBox
                                      sx={{
                                        maxWidth: {
                                          xs: "85%",
                                          md: "70%",
                                        },
                                        minWidth: "120px",
                                        position: "relative",
                                      }}
                                    >
                                      <MDBox
                                        sx={{
                                          backgroundColor: customerMessage
                                            ? "#ffffff"
                                            : "#d9fdd3",
                                          borderRadius: "8px",
                                          px: 1.5,
                                          py: 1,
                                          boxShadow:
                                            "0 1px 1px rgba(0,0,0,0.08)",
                                          border: selectedReply
                                            ? "2px solid #168c7b"
                                            : "2px solid transparent",
                                        }}
                                      >
                                        {message.author_type === "AGENT" ? (
                                          <MDTypography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              lineHeight: 1.3,
                                              mb: 1,
                                            }}
                                          >
                                            You
                                          </MDTypography>
                                        ) : (
                                          <>
                                            {message.author_type ===
                                            "CUSTOMER" ? (
                                              <MDTypography
                                                sx={{
                                                  fontSize: "14px",
                                                  fontWeight: 600,
                                                  lineHeight: 1.3,
                                                  mb: 1,
                                                }}
                                              >
                                                {message.author}
                                              </MDTypography>
                                            ) : (
                                              <>
                                                <MDTypography
                                                  sx={{
                                                    fontSize: "10px",
                                                    color: "text.secondary",
                                                    lineHeight: 1.3,
                                                    mb: 0.25,
                                                  }}
                                                >
                                                  {message.author_type}
                                                </MDTypography>

                                                <MDTypography
                                                  sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    lineHeight: 1.3,
                                                    mb: 1,
                                                  }}
                                                >
                                                  {message.author}
                                                </MDTypography>
                                              </>
                                            )}
                                          </>
                                        )}

                                        {getReplyPreview(message)}

                                        <MDBox
                                          sx={{
                                            fontSize: "16px",
                                            lineHeight: 1.45,
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {renderMessageContent(message)}
                                        </MDBox>

                                        <MDBox
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mt: 0.5,
                                          }}
                                        >
                                          <MDBox
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <MDButton
                                              variant="text"
                                              color="dark"
                                              size="small"
                                              onClick={() =>
                                                handleReply(message)
                                              }
                                              sx={{
                                                minWidth: "auto",
                                                px: 0.5,
                                                py: 0,
                                                fontSize: "11px",
                                                textTransform: "none",
                                              }}
                                            >
                                              <Icon
                                                sx={{
                                                  fontSize: "15px !important",
                                                  mr: 0.25,
                                                }}
                                              >
                                                reply
                                              </Icon>
                                              Reply
                                            </MDButton>

                                            {customerMessage && (
                                              <IconButton
                                                size="small"
                                                onClick={(event) =>
                                                  openReactionPicker(
                                                    event,
                                                    message,
                                                  )
                                                }
                                                sx={{
                                                  width: "26px",
                                                  height: "26px",
                                                  p: 0,
                                                }}
                                              >
                                                <Icon
                                                  sx={{
                                                    fontSize: "17px !important",
                                                  }}
                                                >
                                                  add_reaction
                                                </Icon>
                                              </IconButton>
                                            )}
                                          </MDBox>

                                          <MDBox
                                            sx={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <MDTypography
                                              sx={{
                                                fontSize: "11px",
                                                color: "text.secondary",
                                              }}
                                            >
                                              {formatTime(message.msg_time)}
                                            </MDTypography>

                                            {!customerMessage &&
                                              getMessageStatus(message) && (
                                                <MDTypography
                                                  sx={{
                                                    fontSize: "11px",
                                                    color: message.read
                                                      ? "#53bdeb"
                                                      : "text.secondary",
                                                  }}
                                                >
                                                  {getMessageStatus(message)}
                                                </MDTypography>
                                              )}
                                          </MDBox>
                                        </MDBox>
                                      </MDBox>

                                      {message.reaction?.emoji && (
                                        <MDBox
                                          sx={{
                                            position: "relative",
                                            display: "flex",
                                            justifyContent: customerMessage
                                              ? "flex-start"
                                              : "flex-end",
                                            mt: -0.75,
                                            px: 1,
                                            zIndex: 2,
                                          }}
                                        >
                                          <MDBox
                                            sx={{
                                              minWidth: "28px",
                                              height: "24px",
                                              px: 0.5,
                                              borderRadius: "12px",
                                              backgroundColor: "#ffffff",
                                              border: "1px solid #d9d9d9",
                                              boxShadow:
                                                "0 1px 2px rgba(0,0,0,0.15)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontFamily:
                                                '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
                                              fontSize: "18px",
                                              fontWeight: "normal",
                                              lineHeight: 1,
                                            }}
                                          >
                                            {message.reaction.emoji}
                                          </MDBox>
                                        </MDBox>
                                      )}
                                    </MDBox>
                                  </MDBox>
                                );
                              })}

                              <MDBox ref={messagesEndRef} />
                            </MDBox>
                          )}
                        </MDBox>

                        {isConversationActive() ? (
                          <>
                            {replyTo && (
                              <MDBox
                                sx={{
                                  borderTop: "1px solid #e0e0e0",
                                  backgroundColor: "#f0f2f5",
                                  px: 2,
                                  py: 1,
                                }}
                              >
                                <MDBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    borderLeft: "4px solid #168c7b",
                                    backgroundColor: "#ffffff",
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: "4px",
                                  }}
                                >
                                  <MDBox
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <MDTypography
                                      sx={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#168c7b",
                                      }}
                                    >
                                      Replying to {replyTo.author}
                                    </MDTypography>

                                    <MDBox
                                      sx={{
                                        fontSize: "13px",
                                        mt: 0.25,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {renderMessageContent(replyTo)}
                                    </MDBox>
                                  </MDBox>

                                  <MDButton
                                    variant="text"
                                    color="dark"
                                    onClick={() => setReplyTo(null)}
                                    sx={{
                                      minWidth: "auto",
                                      px: 0.5,
                                    }}
                                    iconOnly
                                    size="small"
                                  >
                                    <Icon>close</Icon>
                                  </MDButton>
                                </MDBox>
                              </MDBox>
                            )}

                            <WhatsAppMessageComposer
                              conversation={selectedConversation}
                              sendMessage={handleSendMessage}
                            />
                          </>
                        ) : (
                          <MDBox
                            sx={{
                              borderTop: "1px solid #e0e0e0",
                              backgroundColor: "#f0f2f5",
                              px: 2,
                              py: 1.5,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              fontSize={14}
                              textAlign="center"
                            >
                              Meta service window closed. Use a template message
                              to resume conversation.
                            </MDTypography>

                            <MDButton
                              variant="contained"
                              color="dark"
                              startIcon={<Icon>send</Icon>}
                              onClick={() => setTemplateDialogOpen(true)}
                              sx={{
                                whiteSpace: "nowrap",
                              }}
                              size="small"
                            >
                              Template Message
                            </MDButton>

                            <WhatsAppTemplateComposer
                              open={templateDialogOpen}
                              onCancel={() => setTemplateDialogOpen(false)}
                              contact={selectedConversation?.contact}
                              templates={templates}
                              sendTemplate={handleSendTemplate}
                            />
                          </MDBox>
                        )}
                      </>
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppNumberConversationsPage;
